import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Bike, MapPin, Package, Phone, X } from "lucide-react";
import { toast } from "sonner";
import { riderApi, type Delivery } from "@/features/delivery/api";
import { formatApiError } from "@/features/auth/formatError";
import { ROUTES } from "@/constants/routes";

const rupee = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function RiderDashboard() {
    const qc = useQueryClient();
    const { data: rider } = useQuery({ queryKey: ["rider", "me"], queryFn: riderApi.me });
    const { data: available = [], isLoading } = useQuery<Delivery[]>({
        queryKey: ["rider", "available"],
        queryFn: riderApi.available,
        refetchInterval: 5000,
        enabled: !!rider?.is_online,
    });
    const { data: active = [] } = useQuery<Delivery[]>({
        queryKey: ["rider", "active"],
        queryFn: riderApi.active,
        refetchInterval: 8000,
    });

    const acceptMut = useMutation({
        mutationFn: (id: string) => riderApi.accept(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rider", "available"] });
            qc.invalidateQueries({ queryKey: ["rider", "active"] });
            toast.success("Order accepted — heading to pickup");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't accept")),
    });
    const rejectMut = useMutation({
        mutationFn: (id: string) => riderApi.reject(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["rider", "available"] });
            toast.success("Order rejected");
        },
        onError: (err) => toast.error(formatApiError(err, "Couldn't reject")),
    });

    return (
        <div className="space-y-4" data-testid="rider-dashboard-page">
            {!rider?.is_online ? (
                <div className="rounded-2xl border border-border/70 bg-card p-6 text-center shadow-soft">
                    <Bike className="mx-auto h-8 w-8 text-brand" />
                    <p className="mt-2 font-bold">You're offline</p>
                    <p className="text-sm text-muted-foreground">
                        Toggle Online in the header to start receiving orders.
                    </p>
                </div>
            ) : null}

            {active.length > 0 ? (
                <section className="space-y-2">
                    <h2 className="text-lg font-bold">Active delivery</h2>
                    {active.map((d) => (
                        <Link
                            key={d.id}
                            to={ROUTES.riderActive}
                            className="block rounded-2xl border border-brand/40 bg-brand/5 p-4 shadow-soft"
                            data-testid={`rider-active-card-${d.id}`}
                        >
                            <div className="flex items-center justify-between">
                                <p className="font-bold">{d.order?.number}</p>
                                <span className="rounded-full bg-brand px-2 py-1 text-[10px] font-bold uppercase text-white">
                                    {d.status.replace(/_/g, " ")}
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-muted-foreground">
                                {d.order?.item_count} items · {rupee(d.order?.total || 0)} · Tap to continue
                            </p>
                        </Link>
                    ))}
                </section>
            ) : null}

            <section>
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-lg font-bold">Available orders</h2>
                    <span className="text-xs text-muted-foreground">
                        {isLoading ? "Refreshing…" : `${available.length} offered`}
                    </span>
                </div>
                {available.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-border/70 bg-card/50 p-6 text-center text-sm text-muted-foreground">
                        {rider?.is_online
                            ? "No orders offered right now. Sit tight — you'll get pinged."
                            : "Go online to see incoming orders."}
                    </div>
                ) : (
                    <div className="space-y-3">
                        {available.map((d) => (
                            <div
                                key={d.id}
                                data-testid={`rider-offer-${d.id}`}
                                className="rounded-2xl border border-border/70 bg-card p-4 shadow-soft"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="font-bold">{d.order?.number}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {d.order?.item_count} items · {rupee(d.order?.total || 0)}
                                        </p>
                                    </div>
                                    <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-[10px] font-bold uppercase text-emerald-700">
                                        Earn {rupee(d.earnings)}
                                    </span>
                                </div>
                                <div className="mt-3 flex items-start gap-2 text-sm">
                                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                                    <div className="min-w-0">
                                        <p className="truncate font-medium">{d.order?.address.full_name}</p>
                                        <p className="text-xs text-muted-foreground line-clamp-2">
                                            {d.order?.address.line1}, {d.order?.address.line2}
                                            {d.order?.address.landmark ? ` · ${d.order.address.landmark}` : ""}
                                        </p>
                                        <p className="text-xs text-muted-foreground">
                                            {d.order?.address.city}, {d.order?.address.pincode}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-4 flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => rejectMut.mutate(d.id)}
                                        disabled={rejectMut.isPending}
                                        data-testid={`rider-reject-${d.id}`}
                                        className="flex-1 rounded-full border border-border py-2 text-sm font-semibold hover:bg-muted"
                                    >
                                        <X className="mr-1 inline-block h-4 w-4" /> Reject
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => acceptMut.mutate(d.id)}
                                        disabled={acceptMut.isPending}
                                        data-testid={`rider-accept-${d.id}`}
                                        className="flex-[1.5] rounded-full bg-brand py-2 text-sm font-bold text-white shadow-brand hover:bg-brand-600"
                                    >
                                        <Package className="mr-1 inline-block h-4 w-4" /> Accept
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <p className="text-center text-[11px] text-muted-foreground">
                Tip: keep the app open while online.{" "}
                <Phone className="inline h-3 w-3" /> Customer support: 1800-TSGO-CARE
            </p>
        </div>
    );
}
